import { Card } from "@verde/compose-ui";
import type { FetchClient } from "@verde/entities";
import { AlertCircleIcon, ExternalLinkIcon, LightIcon } from "@verde/icons";
import { Harvest } from "@verde/modules";
import { Alert, Button, Progress, Skeleton } from "@verde/ui-core";
import { motion } from "framer-motion";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface CreditProfileCapacityProps {
  client?: FetchClient;
  updating?: boolean;
  creditUsageId?: number;
}

const CAROUSEL_SETTINGS = {
  per_page: 2,
};

const DATA = [
  {
    title: "Solicite boas garantias",
    texts: [
      "Identifique junto com seu cliente bons avalistas",
      "Participantes da atividade rural com bom rating devem ser considerados como potencias avalistas",
      "Antecipe e pergunte ao produtor os tipos de garantia que ele estaria disposto a oferecer além de AF da Produção",
    ],
  },
  {
    title: "Valor de financiamento dentro da capacidade do cliente",
    texts: [
      "O valor solicitado para financiamento não deverá passar de 80% do resultado operacional ao declarar os quadro de produção",
      "Caso seja superior, nossas analistas terão que avaliar as particularidades do negócio para aprovar",
    ],
  },
];

function CreditProfileCapacity({
  client,
  updating,
  creditUsageId,
}: CreditProfileCapacityProps) {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(0);
  const itemsLength = useMemo(
    () => Math.ceil(DATA.length / CAROUSEL_SETTINGS.per_page),
    [],
  );

  const isCalculated = useMemo(() => {
    return !!client?.has_proposals;
  }, [client]);

  if (!client?.rating?.id) {
    return null;
  }

  if (isCalculated === false) {
    return (
      <Card
        title="Descubra a capacidade de pagamento desse produtor"
        classNames={{
          base: "border",
        }}
      >
        <Alert
          kind="warning"
          classNames={{
            base: "p-4 bg-off-yellow-bg",
          }}
        >
          <div className="flex items-center gap-2">
            <p className="flex-1">
              Calcule a capacidade de pagamento desse produtor declarando os
              dados de suas áreas, sua receita e produção. Para isso, crie uma
              proposta de financiamento e cadastre os dados solicitados.
            </p>

            <Button
              size="lg"
              icon={<ExternalLinkIcon />}
              onClick={() => {
                navigate("/proposta");
              }}
            >
              Criar proposta
            </Button>
          </div>
        </Alert>
      </Card>
    );
  }

  return (
    <Card
      title="Capacidade de pagamento"
      classNames={{
        base: "border",
      }}
    >
      {updating && (
        <div className="grid gap-6 rounded border border-neutral-light-2 bg-neutral-light-1 p-6">
          <div className="grid gap-1">
            <p className="text-lg font-semibold">Recalculando dados...</p>
          </div>

          <Progress kind="infinite" />
        </div>
      )}

      {!updating && (
        <Harvest.HarvestDebtsSummary
          client_id={client?.id}
          creditUsageId={creditUsageId}
        />
      )}

      <Alert
        icon={<AlertCircleIcon />}
        classNames={{
          base: "p-4 bg-off-yellow-bg border",
          icon: "w-6 h-6 text-neutral-dark-3",
        }}
      >
        <p className="text-base font-medium text-neutral-dark-2">
          Importante: O resultado é apenas uma previsão para ajudar você baseado
          nos dados de produção e áreas declarados e endividamentos encontrados
          no CPF do produtor(a). Para ter certeza solicite uma análise de
          crédito enviando uma proposta de financiamento.
        </p>
      </Alert>

      <Card
        classNames={{
          base: "border",
        }}
        icon={<LightIcon />}
        title="Dicas para solicitar crédito com a Verde:"
      >
        <div className="grid grid-cols-2 gap-4">
          {DATA.slice(
            currentPage * CAROUSEL_SETTINGS.per_page,
            currentPage * CAROUSEL_SETTINGS.per_page +
              CAROUSEL_SETTINGS.per_page,
          ).map(({ title, texts }) => {
            return (
              <motion.div
                key={`capacity-hint-item-${title}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid min-h-[170px] flex-1 items-start gap-2 rounded border border-neutral-light-2 bg-neutral-light-1 p-4"
              >
                <p className="text-base font-bold text-neutral-dark-2">
                  {title}
                </p>

                <ul className="list-disc">
                  {texts.map((text) => (
                    <li
                      key={`capacity-hint-item-text-${text}`}
                      className="mb-0 text-base font-medium text-neutral-dark-1"
                    >
                      {text}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {itemsLength > 1 && (
          <div className="flex items-center justify-center gap-2">
            {new Array(Math.ceil(DATA.length / CAROUSEL_SETTINGS.per_page))
              .fill(undefined)
              .map((_, index) => {
                return (
                  <div
                    key={`slider-dot-item-${Math.random()}`}
                    className={`h-2 w-2 cursor-pointer rounded-full ${currentPage === index ? "bg-neutral-dark-2" : "bg-neutral-light-3"}`.trim()}
                    onClick={() => setCurrentPage(index)}
                  />
                );
              })}
          </div>
        )}
      </Card>
    </Card>
  );
}

export default React.memo(CreditProfileCapacity);
